import { CareTeamRoleDescription } from '@quromedical/fhir-common';
import {
  type CareTeamMembersSchema,
  type CareTeamSearchParams,
  SupportedCareTeamStatus,
} from '@quromedical/models';
import { first, last, startcase } from '@quromedical/utils';
import type { Field, PrimitiveValueType, SelectOption } from 'components/types';
import type { OrganizationFetcher } from 'core/group';
import type { Fetcher } from 'design-system/components';
import { practitionerFetcher } from 'integration';
import { strings } from 'strings';

import type { CreateCareTeamTemplate } from './CareTeamCreateCard';
import type { UpdateCareTeamTemplate } from './CareTeamForm';

export const initialSearch: Partial<CareTeamSearchParams> = {};

const statusFetcher = SupportedCareTeamStatus.map((value) => ({
  value,
  display: startcase(value),
}));

const roleFetcher: Fetcher<PrimitiveValueType, string> = Object.entries(
  CareTeamRoleDescription,
).map((value) => ({
  value: first(value) as string,
  display: startcase(last(value) as string),
}));

export const searchFields: Field<CareTeamSearchParams>[] = [
  {
    subfields: [
      {
        type: 'combobox-single',
        key: 'status',
        label: strings.LabelStatus,
        placeholder: strings.StatusPlaceholder,
        fetcher: [
          {
            display: strings.LabelShowAll,
            value: undefined,
          },
          ...statusFetcher,
        ],
      },
    ],
  },
];

export const fields = (
  organizationFetcher: OrganizationFetcher,
  initialOrganization?: SelectOption,
): Field<UpdateCareTeamTemplate>[] => [
  {
    subfields: [
      {
        type: 'text-box',
        key: 'name',
        label: strings.LabelName,
        placeholder: strings.NamePlaceholder,
      },
      {
        type: 'combobox-single',
        key: 'status',
        label: strings.LabelStatus,
        placeholder: strings.StatusPlaceholder,
        fetcher: statusFetcher,
      },
    ],
  },
  {
    subfields: [
      {
        type: 'combobox-single',
        key: 'managingOrganization',
        label: strings.LabelManagingOrganization,
        searchable: true,
        initialSelection: initialOrganization ? [initialOrganization] : [],
        placeholder: strings.PlaceHolderSelectOrganization,
        fetcher: organizationFetcher,
      },
    ],
  },
];

export const createFields = (
  organizationFetcher: OrganizationFetcher,
  initialOrganization?: SelectOption,
): Field<CreateCareTeamTemplate>[] => [
  {
    subfields: [
      {
        type: 'text-box',
        key: 'name',
        label: strings.LabelName,
        placeholder: strings.NamePlaceholder,
      },
      {
        type: 'combobox-single',
        key: 'status',
        label: strings.LabelStatus,
        placeholder: strings.StatusPlaceholder,
        fetcher: statusFetcher,
      },
    ],
  },

  {
    subfields: [
      {
        type: 'combobox-single',
        key: 'managingOrganization',
        label: strings.LabelManagingOrganization,
        searchable: true,
        initialSelection: initialOrganization ? [initialOrganization] : [],
        placeholder: strings.PlaceHolderSelectOrganization,
        fetcher: organizationFetcher,
      },
    ],
  },
];

export const teamFields = (): Field<CareTeamMembersSchema>[] => [
  {
    heading: 'Team Members',
    subfields: [
      {
        type: 'combobox-single',
        key: 'role',
        label: strings.LabelRole,
        searchable: true,
        fetcher: roleFetcher,
      },
      {
        type: 'combobox-single',
        key: 'member',
        label: strings.TitlePractitioner,
        searchable: true,
        fetcher: practitionerFetcher,
      },
    ],
  },
];
